import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { TextareaModule } from 'primeng/textarea';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import {
  PartnerKycService,
  PartnerKyc,
  KycStatus,
  normalizeKycStatus,
} from '../../services/partner-kyc';
import { PartnerMessageService, PartnerMessageRow } from '../../services/partner-message';
import { FilePreviewService } from '../../shared/file-preview';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

type StatusFilter = 'Pending' | 'Approved' | 'Rejected' | 'All';

@Component({
  selector: 'app-partner-kyc-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    CheckboxModule,
    TextareaModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
  ],
  templateUrl: './partner-kyc-review.html',
  styleUrls: ['./partner-kyc-review.css'],
})
export class PartnerKycReviewPage implements OnInit {
  private svc = inject(PartnerKycService);
  private msgSvc = inject(PartnerMessageService);
  private preview = inject(FilePreviewService);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  readonly statusFilters: StatusFilter[] = ['Pending', 'Approved', 'Rejected', 'All'];

  loading = signal(false);
  search = signal('');
  statusFilter = signal<StatusFilter>('Pending');
  rows = signal<PartnerKyc[]>([]);

  // ---- review dialog ----
  showReview = signal(false);
  reviewing = signal<PartnerKyc | null>(null);
  reviewLoading = signal(false);
  acting = signal(false);

  // verify flags (approve)
  vPan = signal(true);
  vAadhaar = signal(true);
  vGst = signal(true);
  vBank = signal(true);

  // reject
  rejectReason = signal('');
  showReject = signal(false);

  // message channels (PartnerMessage endpoint)
  channels = signal<PartnerMessageRow | null>(null);
  savingChannels = signal(false);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter(
      (r) =>
        (r.partnerName || '').toLowerCase().includes(q) ||
        (r.partnerCode || '').toLowerCase().includes(q) ||
        (r.fullName || '').toLowerCase().includes(q) ||
        (r.panNumber || '').toLowerCase().includes(q),
    );
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    // Deep-link: ?partnerId=5 (from the bell / KYC-pending list) auto-opens that review.
    this.route.queryParams.subscribe((q) => {
      const pid = Number(q['partnerId']);
      if (pid) this.openReviewByPartner(pid);
    });
  }

  /** Open the review dialog directly for a partner (resolves the KYC row first). */
  openReviewByPartner(partnerId: number) {
    this.showReview.set(true);
    this.showReject.set(false);
    this.rejectReason.set('');
    this.channels.set(null);
    this.reviewLoading.set(true);
    this.svc.getByPartner(partnerId).subscribe({
      next: (res: any) => {
        this.reviewLoading.set(false);
        const data = res?.data ?? res ?? {};
        if (!data || !data.partnerId) {
          this.toast.error('No KYC found for this partner');
          this.closeReview();
          return;
        }
        data.kycStatus = normalizeKycStatus(data.kycStatus);
        this.reviewing.set(data);
        this.vPan.set(data.isPANVerified ?? true);
        this.vAadhaar.set(data.isAadhaarVerified ?? true);
        this.vGst.set(data.isGSTVerified ?? true);
        this.vBank.set(data.isBankVerified ?? true);
        this.loadChannels(data.partnerId);
      },
      error: () => {
        this.reviewLoading.set(false);
        this.closeReview();
      },
    });
  }

  // ===================== list =====================
  load() {
    this.loading.set(true);
    const f = this.statusFilter();
    const status = f === 'All' ? undefined : (f as KycStatus);
    this.svc.getAll(status).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        const list = (Array.isArray(data) ? data : []).map((r: any) => ({
          ...r,
          kycStatus: normalizeKycStatus(r.kycStatus),
        }));
        this.rows.set(list);
      },
      error: () => this.loading.set(false),
    });
  }

  onFilter(f: StatusFilter) {
    this.statusFilter.set(f);
    this.load();
  }

  // ===================== review =====================
  openReview(r: PartnerKyc) {
    this.showReview.set(true);
    this.showReject.set(false);
    this.rejectReason.set('');
    this.channels.set(null);
    this.reviewLoading.set(true);
    const id = r.id ?? 0;
    this.svc.getById(id).subscribe({
      next: (res: any) => {
        this.reviewLoading.set(false);
        const data = res?.data ?? res ?? r;
        data.kycStatus = normalizeKycStatus(data.kycStatus);
        this.reviewing.set(data);
        // seed verify flags from any existing values
        this.vPan.set(data.isPANVerified ?? true);
        this.vAadhaar.set(data.isAadhaarVerified ?? true);
        this.vGst.set(data.isGSTVerified ?? true);
        this.vBank.set(data.isBankVerified ?? true);
        this.loadChannels(data.partnerId);
      },
      error: () => {
        this.reviewLoading.set(false);
        this.reviewing.set(r);
        this.loadChannels(r.partnerId);
      },
    });
  }

  closeReview() {
    this.showReview.set(false);
    this.reviewing.set(null);
  }

  approve() {
    const r = this.reviewing();
    if (!r) return;
    this.acting.set(true);
    this.svc
      .approve({
        partnerId: r.partnerId,
        isPANVerified: this.vPan(),
        isAadhaarVerified: this.vAadhaar(),
        isGSTVerified: this.vGst(),
        isBankVerified: this.vBank(),
      })
      .subscribe({
        next: (res: any) => {
          this.acting.set(false);
          if (res?.success === false) {
            this.toast.error(res?.message || 'Failed to approve');
            return;
          }
          this.toast.success(res?.message || 'KYC approved');
          this.closeReview();
          this.load();
        },
        error: () => this.acting.set(false),
      });
  }

  reject() {
    const r = this.reviewing();
    if (!r) return;
    const reason = this.rejectReason().trim();
    if (!reason) {
      this.toast.warning('A rejection reason is required');
      return;
    }
    this.acting.set(true);
    this.svc.reject({ partnerId: r.partnerId, rejectionReason: reason }).subscribe({
      next: (res: any) => {
        this.acting.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to reject');
          return;
        }
        this.toast.success(res?.message || 'KYC rejected');
        this.closeReview();
        this.load();
      },
      error: () => this.acting.set(false),
    });
  }

  /** Preview a stored document (the *FilePath fields hold an S3 guid) in the shared modal. */
  openDoc(guid: string | null | undefined, label = 'Document') {
    this.preview.open(guid, label);
  }

  // ===================== message channels =====================
  private loadChannels(partnerId: number) {
    this.msgSvc.getByPartner(partnerId).subscribe({
      next: (res: any) => {
        const d = res?.data ?? res ?? {};
        this.channels.set({
          partnerId,
          messageTypeId: d.messageTypeId ?? null,
          // No channel row yet => all-off defaults.
          isWhatsApp: !!d.isWhatsApp,
          isSMS: !!d.isSMS,
          isEmail: !!d.isEmail,
          isPaymentReceipt: d.isPaymentReceipt ?? true,
          isCoi: d.isCoi ?? true,
          isOtp: d.isOtp ?? true,
          isPaymentLink: d.isPaymentLink ?? true,
          isPolicyIssued: d.isPolicyIssued ?? true,
          isKyc: d.isKyc ?? true,
        });
      },
      error: () =>
        this.channels.set({ partnerId, messageTypeId: null, isWhatsApp: false, isSMS: false, isEmail: false,
          isPaymentReceipt: true, isCoi: true, isOtp: true, isPaymentLink: true, isPolicyIssued: true, isKyc: true }),
    });
  }

  toggleChannel(key: 'isWhatsApp' | 'isSMS' | 'isEmail') {
    const c = this.channels();
    if (!c) return;
    this.channels.set({ ...c, [key]: !c[key] });
  }

  saveChannels() {
    const c = this.channels();
    if (!c) return;
    if (!c.isWhatsApp && !c.isSMS && !c.isEmail) {
      this.toast.warning('Select at least one channel');
      return;
    }
    this.savingChannels.set(true);
    this.msgSvc
      .save({
        partnerId: c.partnerId,
        isWhatsApp: c.isWhatsApp,
        isSMS: c.isSMS,
        isEmail: c.isEmail,
        isPaymentReceipt: c.isPaymentReceipt,
        isCoi: c.isCoi,
        isOtp: c.isOtp,
        isPaymentLink: c.isPaymentLink,
        isPolicyIssued: c.isPolicyIssued,
        isKyc: c.isKyc,
      })
      .subscribe({
        next: (res: any) => {
          this.savingChannels.set(false);
          if (res?.success === false) {
            this.toast.error(res?.message || 'Failed to save channels');
            return;
          }
          this.toast.success(res?.message || 'Message channels updated');
        },
        error: () => this.savingChannels.set(false),
      });
  }

  // ===================== helpers =====================
  statusClass(s: KycStatus): string {
    switch (s) {
      case 'Approved':
        return 'ok';
      case 'Rejected':
        return 'danger';
      case 'Pending':
        return 'warn';
      default:
        return 'muted';
    }
  }

  /** p-tag severity for a KYC status. */
  statusSeverity(s: KycStatus): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (s) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Pending':
        return 'warn';
      default:
        return 'secondary';
    }
  }
}
